/**
 * useCompetitionData - Data Fetching and Computed Values
 *
 * Single responsibility: Fetching and transforming competition data.
 * Handles all data-related logic for CompetitionView.
 */

import { useMemo } from 'react';
import { useCompetitions } from '@/hooks/useCompetitions';
import { usePickemById } from '@/api/query/usePickem';
import { usePickemSelections } from '@/hooks/usePickemSelections';
import { useSubmitSelections } from '@/api/mutations/useSubmitSelections';
import { formatTimeRemaining, formatRelativeTime } from '@/lib/dateFormat';
import { toQuestionId } from '@/utils/type-guards';

interface UseCompetitionDataProps {
  gameId: string;
  onSuccessSubmit: () => void;
}

/**
 * Hook for managing competition data and computed values
 *
 * @param gameId - The competition ID
 * @param onSuccessSubmit - Callback when submission succeeds
 * @returns All data, computed values, and selection handlers
 */
export function useCompetitionData({ gameId, onSuccessSubmit }: UseCompetitionDataProps) {
  // Data fetching
  const { getCompetition } = useCompetitions();
  const competition = getCompetition(gameId);
  const { data: pickemDetails, isLoading, error } = usePickemById(gameId);

  // Selection management
  const {
    selections,
    handleTeamSelect,
    handlePropSelect,
    handleTieBreakerChange,
    clearSelections,
    quickPick,
    allQuestionsAnswered,
    buildSubmissionPayload,
  } = usePickemSelections(pickemDetails);

  // Submission
  const hasExistingSelections = (pickemDetails?.pickem.selections?.length ?? 0) > 0;
  const submitMutation = useSubmitSelections({
    pickemId: gameId,
    isUpdate: hasExistingSelections,
    showToast: false,
    onSuccess: onSuccessSubmit,
  });

  // Computed values
  const selectionResults = useMemo(() => {
    if (!pickemDetails?.pickem.selections) return {};

    const results: Record<string, boolean> = {};
    pickemDetails.pickem.selections.forEach(selection => {
      if (selection.selection_is_correct !== undefined) {
        results[toQuestionId(selection.question_id)] = selection.selection_is_correct;
      }
    });

    return results;
  }, [pickemDetails]);

  const selectionPoints = useMemo(() => {
    if (!pickemDetails?.pickem.selections) return {};

    const points: Record<string, number> = {};
    pickemDetails.pickem.selections.forEach(selection => {
      points[toQuestionId(selection.question_id)] = selection.points || 0;
    });

    return points;
  }, [pickemDetails]);

  const timeDisplay = useMemo(() => {
    const comp =
      competition ||
      (pickemDetails
        ? {
            state: pickemDetails.pickem.pickem_status === 'COMPLETE' ? 'final' : 'countdown',
            gameDate: pickemDetails.pickem.start_utc || new Date().toISOString(),
          }
        : null);

    if (!comp) return '';

    const gameDate = typeof comp.gameDate === 'string' ? new Date(comp.gameDate) : comp.gameDate;

    if (!(gameDate instanceof Date)) {
      return '';
    }

    switch (comp.state) {
      case 'countdown':
        return formatTimeRemaining(gameDate);
      case 'live':
        return 'Live';
      case 'final':
        return formatRelativeTime(gameDate);
      default:
        return formatRelativeTime(gameDate);
    }
  }, [competition, pickemDetails]);

  const competitionData = useMemo(() => {
    if (competition) return competition;

    // Fallback if competition not in context
    return {
      id: gameId,
      state:
        pickemDetails?.pickem.pickem_status === 'COMPLETE'
          ? ('final' as const)
          : pickemDetails?.pickem.pickem_status === 'IN_PLAY'
            ? ('live' as const)
            : ('countdown' as const),
      gameDate: new Date(pickemDetails?.pickem.start_utc || new Date().toISOString()),
      title: pickemDetails?.pickem.title_en || 'Competition',
      finalScore:
        pickemDetails?.pickem.pickem_status === 'COMPLETE' &&
        typeof pickemDetails?.pickem.user_ranking === 'number' &&
        typeof pickemDetails?.pickem.total_points === 'number'
          ? { rank: pickemDetails.pickem.user_ranking, points: pickemDetails.pickem.total_points }
          : undefined,
    };
  }, [competition, pickemDetails, gameId]);

  // Derived state
  const isViewOnly = competitionData.state === 'live' || competitionData.state === 'final';
  const isResultsMode = competitionData.state === 'final';
  const submitButtonText = hasExistingSelections ? 'Update Selections' : 'Save Changes';

  return {
    // Data
    pickemDetails,
    competitionData,
    isLoading,
    error,

    // Selections
    selections,
    handleTeamSelect,
    handlePropSelect,
    handleTieBreakerChange,
    clearSelections,
    quickPick,
    allQuestionsAnswered,
    buildSubmissionPayload,

    // Submission
    submitMutation,
    hasExistingSelections,
    submitButtonText,

    // Computed values
    selectionResults,
    selectionPoints,
    timeDisplay,

    // Derived state
    isViewOnly,
    isResultsMode,
  };
}
